import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Grid, 
  Paper,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { getAuth } from 'firebase/auth';

const QAStep = ({ data, onUpdate, onNext, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Initialize with 2 pre-filled Q&A or use existing data
  const [commonQuestions, setCommonQuestions] = useState(data.commonQuestions || [
    { 
      question: "What are your business hours?", 
      answer: "Monday - Saturday 7-5 PM" 
    },
    { 
      question: "How do I make an appointment?", 
      answer: "I will give you a call shortly to confirm a time and date that works for you. Thank you" 
    }
  ]);
  
  // State for adding new Q&A
  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');
  
  // State for editing existing Q&A
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [editQuestion, setEditQuestion] = useState('');
  const [editAnswer, setEditAnswer] = useState('');
  
  // Get the current user ID
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  
  const handleAddQuestion = () => {
    if (!newQuestion.trim() || !newAnswer.trim()) {
      setError('Both question and answer are required');
      return;
    }
    
    setCommonQuestions([
      ...commonQuestions,
      { question: newQuestion.trim(), answer: newAnswer.trim() }
    ]);
    
    setNewQuestion('');
    setNewAnswer('');
    setError('');
  };
  
  const handleDeleteQuestion = (index) => {
    const updatedQuestions = [...commonQuestions];
    updatedQuestions.splice(index, 1);
    setCommonQuestions(updatedQuestions);
  };
  
  const handleEditStart = (index) => {
    setEditIndex(index);
    setEditQuestion(commonQuestions[index].question);
    setEditAnswer(commonQuestions[index].answer);
    setEditDialogOpen(true);
  };
  
  const handleEditSave = () => {
    if (!editQuestion.trim() || !editAnswer.trim()) {
      return;
    }
    
    const updatedQuestions = [...commonQuestions];
    updatedQuestions[editIndex] = {
      question: editQuestion.trim(),
      answer: editAnswer.trim()
    };
    
    setCommonQuestions(updatedQuestions);
    setEditDialogOpen(false);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      if (!userId) {
        throw new Error("User ID is missing. Please try logging in again.");
      }
      
      // Validate we have at least one Q&A
      if (commonQuestions.length === 0) {
        throw new Error("Please add at least one question and answer.");
      }
      
      // Create the document reference and data
      const businessRef = doc(db, 'businesses', userId);
      
      // Save to Firestore
      await setDoc(businessRef, {
        commonQuestions,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      setSuccess(true);
      
      // Update parent component
      onUpdate({
        ...data,
        commonQuestions
      });
      
      // Wait a moment to show success message
      setTimeout(() => {
        onNext();
      }, 1000);
    } catch (err) {
      console.error('Error saving Q&A:', err);
      setError(err.message || 'Failed to save Q&A');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Common Questions & Answers
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Q&A saved successfully!</Alert>}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Add common questions and answers that your customers might ask. This will help train the AI to provide accurate responses.
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              Current Q&A ({commonQuestions.length})
            </Typography>
            <List>
              {commonQuestions.map((qa, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={qa.question}
                      secondary={qa.answer}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleEditStart(index)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleDeleteQuestion(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < commonQuestions.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Add New Q&A
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Question"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  placeholder="e.g., What services do you offer?"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Answer"
                  value={newAnswer}
                  onChange={(e) => setNewAnswer(e.target.value)}
                  multiline
                  rows={2}
                  placeholder="e.g., We offer a range of services including..."
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddQuestion}
                  disabled={!newQuestion.trim() || !newAnswer.trim()}
                >
                  Add Question & Answer
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Question & Answer</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Question"
            value={editQuestion}
            onChange={(e) => setEditQuestion(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Answer"
            value={editAnswer}
            onChange={(e) => setEditAnswer(e.target.value)}
            multiline
            rows={3}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} color="primary">Save</Button>
        </DialogActions>
      </Dialog>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button 
          variant="outlined" 
          onClick={onBack} 
          disabled={loading}
        >
          Back
        </Button>
        <Button 
          type="submit" 
          variant="contained" 
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export default QAStep; 